import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { BarcodeIssuesRefusal } from './barcodeIssuesMeta';

const COPY = 'products.barcodeIssues';

type BarcodeIssuesRefusalAlertProps = {
    refusal: BarcodeIssuesRefusal;
    onReload: () => void;
};

/**
 * A fix that didn't go through, above the table (AC-10). A refusal says
 * nothing was fixed, and why: a row code names its product here too, as the
 * row may be scrolled away; a branch refusal reads the products list's own
 * message. `BARCODE_CHANGED` offers **Reload the list**. A lost answer or a
 * server failure says instead that the outcome is unknown and asks to reload
 * before trying again (review of #241, finding 9).
 */
export const BarcodeIssuesRefusalAlert: React.FC<
    BarcodeIssuesRefusalAlertProps
> = ({ refusal, onReload }) => {
    const { t } = useTranslation('pages');

    const description = (() => {
        switch (refusal.kind) {
            case 'row':
                return t(`${COPY}.refused.onRow`, {
                    product: refusal.productName,
                    message: t(`${COPY}.refused.${refusal.code}`),
                });
            case 'general':
                return t(`${COPY}.refused.${refusal.code}`);
            case 'changed':
                return refusal.productName === null
                    ? t(`${COPY}.refused.BARCODE_CHANGED`)
                    : t(`${COPY}.refused.BARCODE_CHANGED_row`, {
                          product: refusal.productName,
                      });
            case 'branch':
                return t('products.components.productTable.errorMessage');
            case 'refused':
                return t(`${COPY}.refused.other`);
            case 'unknown':
                return t(`${COPY}.refused.unknown`);
        }
    })();
    const isUnknown = refusal.kind === 'unknown';

    return (
        <Alert
            status={isUnknown ? 'warning' : 'danger'}
            {...getNodeTestIdProp('barcode-issues-refused')}
        >
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>
                    {isUnknown
                        ? t(`${COPY}.refused.unknownTitle`)
                        : t(`${COPY}.refused.title`)}
                </Alert.Title>
                <Alert.Description>{description}</Alert.Description>
            </Alert.Content>
            {refusal.kind === 'changed' || isUnknown ? (
                <Button
                    onPress={onReload}
                    size="sm"
                    variant="secondary"
                    {...getNodeTestIdProp('barcode-issues-reload')}
                >
                    {t(`${COPY}.refused.reload`)}
                </Button>
            ) : null}
        </Alert>
    );
};
